import { Journey } from 'src/common/interfaces/journey.interface';
import { Node } from 'src/common/interfaces/node.interface';
import { Organization } from 'src/common/interfaces/organization.interface';
import { Workspace } from 'src/common/interfaces/workspace.interface';
import { Nullable } from 'src/types/nullable.type';
import { apiAddNodeToJourney, apiCreateOrUpdateNode } from 'src/utils/journeyApi';
import { create } from 'zustand';
import { WizardContentCardsType } from './content/screen';

const PLACEHOLDER_CARD_COUNT = 7;
export const WIZARD_INITIAL_PLACEHOLDER_CARDS = Array(PLACEHOLDER_CARD_COUNT).fill(null);

interface Methods {
  setJourney: (journey: Journey) => void;
  createJourneyWithSteps: () => void;
  setWorkspaceAndOrganization: (organization: Organization, workspace: Workspace) => void;
  setProgress: (progress: number) => void;
  resetStore: () => void;
  increaseOngoingUploadCount: (value: number) => void;
  decreaseOngoingUploadCount: () => void;
  removeNodeCreationFromStackByIndex: (index: number) => void;
  stackNodeCreation: (contentType: string, payload?: object) => void;
  setContentCards: (cards: WizardContentCardsType) => void;
}

interface Variables {
  journey: Nullable<Journey>;
  workspaceId: Nullable<Workspace['id']>;
  currentOrganizationId: Nullable<Organization['id']>;
  isWizardCompleted: boolean;
  ongoingUploadCount: number;
  nodeCreationStack: Array<() => Promise<Node>>;
  progress: number;
  contentCards: WizardContentCardsType;
}

interface PrivateMethods {
  initialProgress: (count: number) => void;
}

type StoreState<P = {}> = Variables & Methods & P;

const initialState: Variables = {
  journey: null,
  workspaceId: null,
  currentOrganizationId: null,
  nodeCreationStack: [],
  ongoingUploadCount: 0,
  isWizardCompleted: false,
  progress: 0,
  contentCards: [...WIZARD_INITIAL_PLACEHOLDER_CARDS],
};

export const useWizardStore = create<StoreState>((set, get) => ({
  ...initialState,
  setWorkspaceAndOrganization: (organization, workspace) => {
    set({ currentOrganizationId: organization.id, workspaceId: workspace.id });
  },
  setContentCards(contentCards) {
    set({ contentCards });
  },
  setProgress(progress) {
    set({ progress });
  },
  increaseOngoingUploadCount(count) {
    const { ongoingUploadCount } = get();
    set({ ongoingUploadCount: ongoingUploadCount + count });
  },
  decreaseOngoingUploadCount() {
    const { ongoingUploadCount } = get();
    set({ ongoingUploadCount: ongoingUploadCount - 1 });
  },
  setJourney(journey) {
    set({ journey });
    const { initialProgress } = get() as StoreState<PrivateMethods>;
    initialProgress(3);
  },
  initialProgress(count: number) {
    const initialProgressTimeoutId = setTimeout(() => {
      set({ progress: 10 });
      clearTimeout(initialProgressTimeoutId);
    }, 350);
    let internalCount = 0;
    const dummyProgressIncrementIntervalId = setInterval(() => {
      if (internalCount === count) {
        clearInterval(dummyProgressIncrementIntervalId);
      }
      const { progress } = get();
      set({ progress: progress + Math.floor(Math.random() * (14 - 2 + 1) + 2) });
      internalCount += 1;
    }, 700);
  },
  createJourneyWithSteps: async () => {
    const { journey } = get();
    if (journey) {
      const { nodeCreationStack, progress } = get();
      const remainingProgressForNodes = 100 - progress;
      const [stepToAddNode] = journey.steps;
      for (const creator of nodeCreationStack) {
        const node = await creator();
        const { progress } = get();
        await apiAddNodeToJourney(node.uuid, stepToAddNode.id, null);
        set({ progress: Math.min(Math.ceil(progress + remainingProgressForNodes / nodeCreationStack.length), 80) });
      }
      set({ isWizardCompleted: true });
    }
  },
  stackNodeCreation(contentType, payload) {
    const { nodeCreationStack, currentOrganizationId, workspaceId } = get();
    nodeCreationStack.push(() => apiCreateOrUpdateNode(currentOrganizationId, workspaceId, contentType, null, payload));
  },
  removeNodeCreationFromStackByIndex(index) {
    const { nodeCreationStack } = get();
    set({ nodeCreationStack: nodeCreationStack.filter((_, nIndex) => nIndex !== index) });
  },
  resetStore() {
    set({ ...initialState, nodeCreationStack: [], contentCards: [...WIZARD_INITIAL_PLACEHOLDER_CARDS] });
  },
}));
